import clsx from "clsx";
import { FC, Fragment, useEffect, useState } from "react";

import { ControlsApi, DATA_E2E_KEY, type IEnvFlagsDto } from "@ryde/shared";

import {
  StyledApiInfoPanel,
  StyledEnvFlagsGrid,
  StyledVersionContainer,
} from "./controls.panels.styled";
import { ENV_FLAG_KEYS } from "./controls.parts.models";

export const ApiVersionPanel: FC = () => {
  const [apiVersion, setApiVersion] = useState<string>("");
  const [envFlags, setEnvFlags] = useState<IEnvFlagsDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const getVersionClassName = (): string => {
    return clsx("version-value", {
      loading: isLoading,
      error: !!error,
    });
  };

  const getVersionText = (): string => {
    if (isLoading) {
      return "Loading...";
    }

    if (error) {
      return "Error";
    }

    if (!apiVersion || apiVersion === "Unknown") {
      return "No version";
    }

    return apiVersion;
  };

  useEffect(() => {
    const fetchInfo = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError("");
        const [versionRes, flagsRes] = await Promise.all([
          ControlsApi.getApiDevVersion(),
          ControlsApi.getEnvFlags(),
        ]);
        setApiVersion(versionRes?.version ?? "");
        setEnvFlags(flagsRes ?? null);
      } catch (err) {
        setError("Failed to fetch API version");
        setApiVersion("");
        setEnvFlags(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInfo();
  }, []);

  return (
    <StyledApiInfoPanel>
      <h2 {...{ [DATA_E2E_KEY]: "api-info-title" }}>API Information</h2>
      <StyledVersionContainer>
        <span
          className="version-label"
          {...{ [DATA_E2E_KEY]: "api-version-label" }}
        >
          API Version:
        </span>
        <span
          className={getVersionClassName()}
          {...{ [DATA_E2E_KEY]: "api-version-value" }}
        >
          {getVersionText()}
        </span>
      </StyledVersionContainer>
      {envFlags && (
        <StyledEnvFlagsGrid>
          {ENV_FLAG_KEYS.map(key => (
            <Fragment key={key}>
              <span className="env-flag-label">{key}</span>
              <span className="env-flag-value">{envFlags[key]}</span>
            </Fragment>
          ))}
        </StyledEnvFlagsGrid>
      )}
    </StyledApiInfoPanel>
  );
};
